import {
    AddressType,
    CompanySelfService,
    ContractSelfService,
    GetResumeSelfServiceOutput,
    Plan,
    Policy,
} from '../../apiTypes';

export const name = 'selfService';

export const types = {
    SET_PLAN: `${name}/SET_PLAN`,
    SET_PLANS: `${name}/SET_PLANS`,
    SET_CONTRACT: `${name}/SET_CONTRACT`,
    SET_COMPANY: `${name}/SET_COMPANY`,
    SET_POLICY: `${name}/SET_POLICY`,
    SET_PAIEMENT_CB: `${name}/SET_PAIEMENT_CB`,
    SET_PAIEMENT_MODE: `${name}/SET_PAIEMENT_MODE`,
    SET_LAST_VALIDATED_STEP: `${name}/SET_LAST_VALIDATED_STEP`,
    SET_REDIRECT: `${name}/SET_REDIRECT`,
    RESUME: `${name}/RESUME`,
    CLEAR_ALL: `${name}/CLEAR_ALL`,
};

export interface SelfServiceState {
    plan: string;
    plans: Plan[];
    company: CompanySelfService;
    contract: ContractSelfService;
    policy: Policy;
    paiementCB: string;
    paiementMode: string;
    lastValidatedStep: number;
    redirect: boolean;
}

// InitialState
const initialState: SelfServiceState = {
    plan: '',
    plans: [],
    company: null,
    contract: null,
    policy: null,
    paiementCB: '',
    paiementMode: '',
    lastValidatedStep: -1,
    redirect: false,
};

const reducer = (state = initialState, action: any = {}): any => {
    switch (action.type) {
        case types.SET_PLAN:
            return {
                ...state,
                plan: action.payload.plan,
                ...(state.lastValidatedStep < 1 && { lastValidatedStep: 1 }),
            };
        case types.SET_PLANS:
            // eslint-disable-next-line no-case-declarations
            const plan: Plan = action.payload.plans.find((p) => p.entity_id === state.plan);
            // eslint-disable-next-line no-case-declarations
            const defaultPolicy: any = { ...state.policy };
            if (!state.plans.length) {
                if (plan?.travel_policy_settings?.ride_reason_obligation?.default_value) {
                    defaultPolicy.travel_reason_mandatory = true;
                }
                if (plan?.travel_policy_settings?.cost_center_obligation?.default_value) {
                    defaultPolicy.cost_center_mandatory = true;
                }
                if (plan?.travel_policy_settings?.book_for_group_members?.default_value) {
                    defaultPolicy.book_for_group_members = true;
                }
                if (plan?.travel_policy_settings?.book_for_group_guests?.default_value) {
                    defaultPolicy.book_for_group_guests = true;
                }
                if (plan?.travel_policy_settings?.tip_restriction?.default_value) {
                    defaultPolicy.tip_not_allowed = true;
                }

                return {
                    ...state,
                    ...(!state.plans.length && { policy: defaultPolicy }),
                    plans: action.payload.plans,
                };
            }
            // we change plan
            if (!plan?.travel_policy_settings?.ride_reason_obligation?.allowed) {
                defaultPolicy.travel_reason_mandatory = false;
            }
            if (!plan?.travel_policy_settings?.cost_center_obligation?.allowed) {
                defaultPolicy.cost_center_mandatory = false;
            }
            if (!plan?.travel_policy_settings?.book_for_group_members?.allowed) {
                defaultPolicy.book_for_group_members = false;
            }
            if (!plan?.travel_policy_settings?.book_for_group_guests?.allowed) {
                defaultPolicy.book_for_group_guests = false;
            }
            if (!plan?.travel_policy_settings?.tip_restriction?.allowed) {
                defaultPolicy.tip_not_allowed = false;
            }
            if (
                !plan?.travel_policy_settings?.calendar_restrictions?.allowed &&
                defaultPolicy?.restrictions?.time_slots
            ) {
                defaultPolicy.restrictions.time_slots = {};
            }
            if (!plan?.travel_policy_settings?.geo_restrictions?.allowed && defaultPolicy?.restrictions?.areas) {
                defaultPolicy.restrictions.areas = [];
            }

            return {
                ...state,
                policy: defaultPolicy,
                plans: action.payload.plans,
            };

        case types.SET_CONTRACT:
            return {
                ...state,
                contract: action.payload.contract,
            };
        case types.SET_COMPANY:
            return {
                ...state,
                company: action.payload.company,
                ...(state.lastValidatedStep < 2 && { lastValidatedStep: 2 }),
            };
        case types.SET_POLICY:
            return {
                ...state,
                policy: action.payload.policy,
                ...(state.lastValidatedStep < 3 && { lastValidatedStep: 3 }),
            };
        case types.SET_PAIEMENT_CB:
            return {
                ...state,
                paiementCB: action.payload.paiementCB,
            };
        case types.SET_PAIEMENT_MODE:
            return {
                ...state,
                paiementMode: action.payload.paiementMode,
                ...(state.lastValidatedStep < 4 && { lastValidatedStep: 4 }),
            };
        case types.SET_LAST_VALIDATED_STEP:
            return {
                ...state,
                lastValidatedStep: action.payload.step,
            };
        case types.SET_REDIRECT:
            return {
                ...state,
                redirect: action.payload.redirect,
            };
        case types.RESUME:
            return resumeSelfService(state, action.payload.datas);
        case types.CLEAR_ALL:
            return initialState;
        default:
            return state;
    }
};
export default reducer;

// ActionCreators
export const actions = {
    setPlan: (plan: string): any => ({ type: types.SET_PLAN, payload: { plan } }),
    setPlans: (plans: Plan[]): any => ({ type: types.SET_PLANS, payload: { plans } }),
    setPolicy: (policy: Policy): any => ({ type: types.SET_POLICY, payload: { policy } }),
    setContract: (contract: ContractSelfService): any => ({ type: types.SET_CONTRACT, payload: { contract } }),
    setCompany: (company: CompanySelfService): any => ({ type: types.SET_COMPANY, payload: { company } }),
    setPaiementCB: (cb: string): any => ({ type: types.SET_PAIEMENT_CB, payload: { paiementCB: cb } }),
    setPaiementMode: (paiementMode: string): any => ({ type: types.SET_PAIEMENT_MODE, payload: { paiementMode } }),
    setLastValidatedStep: (step: number): any => ({ type: types.SET_LAST_VALIDATED_STEP, payload: { step } }),
    setRedirect: (redirect: boolean): any => ({ type: types.SET_REDIRECT, payload: { redirect } }),
    resume: (datas: GetResumeSelfServiceOutput): any => ({
        type: types.RESUME,
        payload: { datas },
    }),
    resetAll: (): any => ({ type: types.CLEAR_ALL }),
};

const localState = (state: any): SelfServiceState => state[name] || initialState;
export const selectors = {
    plan: (state): string => localState(state).plan,
    plans: (state): Plan[] => localState(state).plans,
    currentPlan: (state): Plan => localState(state).plans.find((p) => p.entity_id === localState(state).plan),
    policy: (state): Policy => localState(state).policy,
    contract: (state): ContractSelfService => localState(state).contract,
    company: (state): CompanySelfService => localState(state).company,
    paiementCB: (state): string => localState(state).paiementCB,
    paiementMode: (state): string => localState(state).paiementMode,
    lastValidatedStep: (state): number => localState(state).lastValidatedStep,
    redirect: (state): boolean => localState(state).redirect,
};

//= =======================================//
//= ============== FUNCTION ===============//
//= =======================================//

function resumeSelfService(state: SelfServiceState, datas: GetResumeSelfServiceOutput): SelfServiceState {
    const {
        plan_selected: planSelected,
        company,
        contract,
        policy,
        payment_policy: paymentPolicy,
        payment_methods: paymentMethods,
    } = datas;

    let lastValidatedStep = 0;
    // if ([SelfServiceStep.CARD_ADDED, SelfServiceStep.PAYMENT_SETUP_DONE, SelfServiceStep.SIGNED].includes(lastStep)) {
    //     lastValidatedStep = 4;
    // } else if ([SelfServiceStep.TRAVEL_POLICY_VALIDATED].includes(lastStep)) {
    //     lastValidatedStep = 3; // CB
    // } else if ([SelfServiceStep.COMPANY_INFO_SENT].includes(lastStep)) {
    //     lastValidatedStep = 2;
    // } else if ([SelfServiceStep.PLAN_SELECTED].includes(lastStep)) {
    //     lastValidatedStep = 1;
    // }
    if (paymentPolicy && paymentMethods) {
        // lastValidatedStep = 4; // Contract
        lastValidatedStep = 3; // CB
    } else if (!paymentPolicy && !paymentMethods) {
        lastValidatedStep = policy.revision <= 2 ? 2 : 3; // Politiques voyages ou  CB
    } else if (company?.name && company?.address) {
        lastValidatedStep = 1; // Entreprise
    } else if (planSelected) {
        lastValidatedStep = 0; // Plans
    }

    return {
        ...initialState,
        plan: planSelected ?? '',
        plans: state.plans,
        contract,
        ...(company && {
            company: {
                name: company.name,
                address: { location: null, type: AddressType.ADDRESS, details: company.address },
            },
        }),
        ...(policy && { policy }),
        ...(paymentPolicy && { paiementMode: paymentPolicy[0]?.payment_mode }),
        lastValidatedStep,
        redirect: true,
    };
}
